import { sanitizeFileName } from '../calendarService';
import { sendReportEmail } from '../cloudService';
import { heuteBerlin } from '../dateService';
import { toBase64Utf8 } from '../emlBuilder';
import {
    FARBE_FEHLER,
    FARBE_LEER,
    FARBE_WARNUNG,
    TD,
    esc,
    formatDate,
    renderGruenerKasten,
    renderKastenHinweis,
    renderMailRahmen,
    renderOutlookKasten,
    renderProjektZelle,
    renderTabelle,
} from '../mailLayout';
import type {
    KickoffBereich,
    KickoffHinweis,
    KickoffHinweisArt,
    KickoffPlan,
    KickoffTermin,
    KickoffTerminArt,
} from './kickoffTypes';
import { generateKickoffIcs } from './projectKickoff';

/**
 * Kickoff-Mail: Wer ein Projekt zugewiesen bekommt, erhält EINE Mail mit allen
 * neuen Projekten, den Kalenderterminen und einer ICS-Datei für Outlook.
 * Bauplan: docs/projekt-kickoff-plan.md §4.2/§4.2a.
 *
 * Aussehen wie die Vertretungsmail (Entscheid Betreiber 11.10.), deshalb
 * dieselben Bausteine aus `mailLayout.ts`. Versand über `/api/send-email`;
 * Antworten landen per Reply-To beim angemeldeten Absender — die API nimmt
 * ihn aus der Anmeldung, nicht aus dem Aufruf.
 */

export interface KickoffMailEingabe {
    empfaengerEmail: string;
    empfaengerName: string;
    /** Ein oder mehrere Projekte für DIESEN Empfänger (Sammelmail beim CSV-Import). */
    plaene: KickoffPlan[];
    erstellerName?: string;
    /** Ziel des Knopfs „App öffnen“, z. B. `window.location.origin`. */
    appUrl: string;
}

const TERMIN_ART: Record<KickoffTerminArt, string> = {
    vorank: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    begehung: 'Begehung',
    einweisung: 'Einweisung',
};

/**
 * Rot = etwas fehlt oder ist schon zu spät, Orange = Handarbeit nötig.
 * Dieselben Farben wie „Turnus manuell“ in der Vertretungsmail.
 */
const HINWEIS_FARBE: Record<KickoffHinweisArt, string> = {
    baustart_fehlt: FARBE_FEHLER,
    frist_ueberschritten: FARBE_FEHLER,
    turnus_manuell: FARBE_WARNUNG,
    ueberschneidung: FARBE_WARNUNG,
    sigeplan_ohne_platz: FARBE_WARNUNG,
};

const BEREICH_TEXT: Record<Exclude<KickoffBereich, 'alle'>, string> = {
    planung: 'Bereich Planung',
    ausfuehrung: 'Bereich Ausführung (Begehungen)',
};

/** Betreff und Dateiname vertragen keine Zeilenumbrüche aus Nutzereingaben. */
const einzeilig = (value: string | undefined | null): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const projektTitel = (p: KickoffPlan, trenner: string): string =>
    [einzeilig(p.projectNumber), einzeilig(p.projectName)].filter(Boolean).join(trenner);

const hatTermine = (plaene: KickoffPlan[]): boolean => plaene.some(p => (p.termine ?? []).length > 0);

export const buildKickoffSubject = (plaene: KickoffPlan[]): string => {
    if (plaene.length === 1) {
        const titel = projektTitel(plaene[0], ' ');
        return titel ? `Neues Projekt: ${titel}` : 'Neues Projekt';
    }
    return `Neue Projekte: ${plaene.length} Projekte`;
};

/**
 * Ein Projekt → `Projekt_<nr>_<name>.ics`, mehrere → `Neue_Projekte_<Datum>.ics`.
 * `heute` ist nur für Tests überschreibbar; sonst der Berliner Kalendertag.
 */
export const buildKickoffIcsFilename = (plaene: KickoffPlan[], heute: string = heuteBerlin()): string => {
    if (plaene.length === 1) {
        const titel = projektTitel(plaene[0], '_');
        return titel ? `Projekt_${sanitizeFileName(titel)}.ics` : 'Projekt.ics';
    }
    return `Neue_Projekte_${sanitizeFileName(heute)}.ics`;
};

const renderHinweis = (h: KickoffHinweis): string =>
    `<div style="font-size:12px;color:${HINWEIS_FARBE[h.art] ?? FARBE_WARNUNG};">${esc(h.text)}</div>`;

const renderTermin = (t: KickoffTermin): string =>
    `<div style="white-space:nowrap;"><span style="font-weight:600;">${esc(TERMIN_ART[t.art] ?? t.art)}</span> ${esc(t.anzeige)}</div>`
    + (t.hinweise ?? []).map(renderHinweis).join('');

/** Termine je Zeile mit ihren Hinweisen, danach die Hinweise zum Projekt. */
const renderTermine = (p: KickoffPlan): string => {
    const termine = p.termine ?? [];
    const teile = termine.length > 0
        ? termine.map(renderTermin)
        : [`<div style="color:${FARBE_LEER};">keine Kalendertermine</div>`];
    return [...teile, ...(p.hinweise ?? []).map(renderHinweis)].join('');
};

const renderBaustart = (p: KickoffPlan): string =>
    (p.baustart ? formatDate(p.baustart) : `<span style="color:${FARBE_FEHLER};">fehlt</span>`)
    + (p.bauende ? `<br>bis ${formatDate(p.bauende)}` : '');

/**
 * Bauherr und Auftraggeber sind oft dieselbe Stelle; dann nur einmal. Weichen
 * sie ab, steht der Auftraggeber beschriftet darunter, damit klar ist, wer wer ist.
 */
const renderBauherr = (p: KickoffPlan): string => {
    const bauherr = einzeilig(p.bauherr);
    const auftraggeber = einzeilig(p.auftraggeber);
    if (!bauherr && !auftraggeber) return '-';
    if (!bauherr || !auftraggeber || bauherr.toLowerCase() === auftraggeber.toLowerCase()) {
        return esc(bauherr || auftraggeber);
    }
    return `${esc(bauherr)}<div style="font-size:12px;color:#64748b;">Auftraggeber: ${esc(auftraggeber)}</div>`;
};

/** Knopf nur für echte Web-Adressen — `appUrl` landet als Link in einer fremden Mailbox. */
const renderAppKnopf = (appUrl: string): string => {
    const url = (appUrl || '').trim();
    if (!/^https?:\/\//i.test(url)) return '';
    return `<div style="margin-top:12px;"><a href="${esc(url)}" style="display:inline-block;background:#059669;color:#ffffff;text-decoration:none;font-weight:700;font-size:14px;padding:8px 16px;border-radius:8px;">App öffnen</a></div>`;
};

export const buildKickoffHtml = (e: KickoffMailEingabe): string => {
    const plaene = e.plaene ?? [];
    const anzahl = plaene.length;
    const ersteller = einzeilig(e.erstellerName);
    const name = einzeilig(e.empfaengerName);

    // Ein gemeinsamer Bereich gehört in den Satz; bei gemischten Bereichen
    // (Sammelmail) steht er stattdessen je Projekt in der Tabelle.
    const bereiche = Array.from(new Set(plaene.map(p => p.bereich)));
    const gemeinsamerBereich = bereiche.length === 1 ? bereiche[0] : null;
    const bereichZusatz = gemeinsamerBereich && gemeinsamerBereich !== 'alle'
        ? ` &ndash; ${BEREICH_TEXT[gemeinsamerBereich]}`
        : '';
    const bereichJeZeile = bereiche.length > 1;

    const satz = anzahl === 1
        ? `Ihnen wurde <strong>ein Projekt</strong> zugewiesen${bereichZusatz}.`
        : `Ihnen wurden <strong>${anzahl} Projekte</strong> zugewiesen${bereichZusatz}.`;

    const anrede = `<p style="margin:0 0 16px;">Hallo${name ? ` ${esc(name)}` : ''},</p>
    <p style="margin:0 0 20px;">
        ${satz}
        ${ersteller ? `Angelegt von ${esc(ersteller)}.` : ''}
    </p>`;

    const zeilen = plaene.map(p => {
        const bereichZeile = bereichJeZeile && p.bereich !== 'alle'
            ? `\n                <div style="font-size:12px;color:#065f46;">${BEREICH_TEXT[p.bereich]}</div>`
            : '';
        return `
        <tr>
            <td style="${TD}">
                ${renderProjektZelle(einzeilig(p.projectName) || 'Projekt', p.projectNumber, p.projectAddress)}${bereichZeile}
            </td>
            <td style="${TD}white-space:nowrap;">${renderBaustart(p)}</td>
            <td style="${TD}font-size:13px;">${renderTermine(p)}</td>
            <td style="${TD}font-size:13px;color:#475569;">${renderBauherr(p)}</td>
        </tr>`;
    }).join('');

    const bestaetigen = 'Danach in der App bestätigen, dass die Termine im Kalender stehen.';
    const knopf = renderAppKnopf(e.appUrl);

    // Ohne Termine hängt keine ICS an; die Outlook-Anleitung liefe ins Leere.
    // Die Bestätigung in der App bleibt trotzdem Pflicht.
    const kasten = hatTermine(plaene)
        ? renderOutlookKasten({
            zusatzSchritte: [bestaetigen],
            zusatz: [
                renderKastenHinweis([
                    'Die Serien (Begehungen, SiGe-Plan) haben bewusst kein Enddatum, weil Projekte oft länger laufen.',
                ]),
                knopf,
            ].filter(Boolean).join('\n        '),
        })
        : renderGruenerKasten(
            'In der App bestätigen',
            `<div style="font-size:14px;color:#334155;">Es stehen noch keine Kalendertermine fest, deshalb hängt keine Kalenderdatei an. Bitte trotzdem in der App bestätigen, dass Sie die Zuweisung gesehen haben.</div>${knopf ? `\n        ${knopf}` : ''}`,
        );

    return renderMailRahmen({
        titel: anzahl === 1 ? 'Neues Projekt' : 'Neue Projekte',
        unterzeile: formatDate(heuteBerlin()),
        bloecke: [
            anrede,
            renderTabelle(['Projekt', 'Baustart', 'Termine im Kalender', 'Bauherr / Auftraggeber'], zeilen),
            kasten,
        ],
        antwortSatz: `Antworten gehen an ${ersteller ? esc(ersteller) : 'den Absender'}.`,
    });
};

/**
 * Verschickt die Kickoff-Mail an EINEN Empfänger. Wirft nie: Das Projekt ist
 * bereits angelegt, die Quittungszeile existiert (Trigger) — ein Mailfehler
 * wird nur vermerkt (`kickoff_mail_vermerken`) und darf die Anlage nicht
 * mitreißen.
 */
export const sendKickoffMail = async (e: KickoffMailEingabe): Promise<{ ok: boolean; error?: string }> => {
    const email = einzeilig(e?.empfaengerEmail);
    if (!email) return { ok: false, error: 'Keine E-Mail-Adresse hinterlegt.' };
    const plaene = e.plaene ?? [];
    if (plaene.length === 0) return { ok: false, error: 'Keine Projekte für die Mail.' };

    try {
        const icsFilename = buildKickoffIcsFilename(plaene);
        // ICS nur mit mindestens einem Termin — eine leere Kalenderdatei
        // verwirrt Outlook und den Empfänger.
        const ics = hatTermine(plaene)
            ? { icsBase64: toBase64Utf8(generateKickoffIcs(plaene)), icsFilename }
            : {};
        await sendReportEmail({
            recipients: [email],
            subject: buildKickoffSubject(plaene),
            html: buildKickoffHtml(e),
            // Pflichtfeld der API, wird nur für einen PDF-Anhang benutzt.
            filename: icsFilename,
            ...ics,
        });
        return { ok: true };
    } catch (err) {
        const meldung = err instanceof Error ? err.message : '';
        return { ok: false, error: meldung || 'Unbekannter Fehler beim Versand.' };
    }
};
